import React, { useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { makeStyles, CircularProgress } from '@material-ui/core';
import clsx from 'clsx';
import { RobotHealthCondition, AlertStatus } from '@moderepo/mode-apis';
import { capitalizeAll, capitalizeFirst, getRobotHealthConditionName, GenericMessageBox } from '@moderepo/mode-app-components';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { fetchRobotCatalog, rcDataContext, selectRobotById, selectRobotCatalog } from '@moderepo/mode-data-state-robot-cloud';
import { modeUIContext, selectSlideOutPanelOptions, showSlideOutPanel, SlideOutPanelLocation } from '@moderepo/mode-ui-state';
import { useRitzTranslation } from '../../utils/customHooks';
import { RCAlertStatusesTable } from '../../components/robot-cloud';
import * as RCUtils from './RCUtils';
import { RCAlertStatusesTableDataItem, RCAlertStatusesTableProps } from '../../components/robot-cloud/RCAlertStatusesTable';
import { getDateTimeAndElapsedTime } from '../containerUtils';
import { isAlertPreviewCompOptions, RCAlertPreviewCompOptions, RCSlideOutPanelCompType } from './RCProjectConfig';


const useStyle = makeStyles(() => {
    return {
        alertsStatusPanel: {
        },
    };
}, { name: 'RCRobotStatusContainer' });



interface RCRobotStatusContainerProps {
    readonly className?: string;
    readonly projectId: number;
    readonly homeId: number;
    readonly robotId: string;
    readonly userId?: number;                // these 2 will only be available if the user is on the User/Home/Device page
    readonly robotClass: string;
    readonly title?: string;
}



/**
 * This is a DUMB component used for displaying health status of ONE device. This component does not have any logic. All it does it display
 * data based on the props provided by the container. All action done in this component will be dispatched back to the
 * container's handler.
 */
export const RCRobotStatusContainer = (props: RCRobotStatusContainerProps) => {
    const { state:dataState } = useContext(ritzDataContext);
    const { state:rcDataState, dispatch:rcDataDispatch } = useContext(rcDataContext);
    const { state:uiState, dispatch:uiDispatch } = useContext(modeUIContext);
    const project = selectProjectById(dataState, props.projectId);

    const robot = selectRobotById(rcDataState, props.homeId, props.robotId);
    const robotCatalog = selectRobotCatalog(rcDataState, props.projectId);
    const [selectedAlertStatusId, setSelectedAlertStatusId] = useState<number | undefined>();
    const slideOutPanelOptions = selectSlideOutPanelOptions(uiState);
    const { trans } = useRitzTranslation(project);


    // Calculate the Device's overall health's condition based on the robotHealthStatus
    const robotHealthCondition = useMemo(() => {
        if (robot?.status) {
            // take the most severe alertStatus from robotHealthStatus.alertStatuses and use that as the robot's overall health status
            return robot.status.alertStatuses.reduce((result: RobotHealthCondition, alertStatus: AlertStatus): RobotHealthCondition => {
                if (alertStatus.severity > result) {
                    // The alert status has higher severity level, use it as the device's health
                    return alertStatus.severity as unknown as RobotHealthCondition;
                }
                return result;
            }, RobotHealthCondition.NORMAL);
        }
        return RobotHealthCondition.UNKNOWN;
    }, [robot]);


    const classes = useStyle();



    /**
     * This useEffect is used for loading robot definition if it is not loaded
     */
    useEffect(() => {
        if (!robotCatalog) {
            rcDataDispatch(fetchRobotCatalog(props.projectId)).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [robotCatalog, props.projectId, rcDataDispatch]);



    // This useEffect is used for checking the slideOutPanelOptions to see if is isAlertPreviewCompOptions.
    // if it is, automatically set the alertId from the isAlertPreviewCompOptions
    useEffect(() => {
        if (isAlertPreviewCompOptions(slideOutPanelOptions) && slideOutPanelOptions.compProps.data.alertId !== undefined) {
            setSelectedAlertStatusId(slideOutPanelOptions.compProps.data.alertId);
        } else {
            setSelectedAlertStatusId(undefined);
        }
    }, [slideOutPanelOptions]);



    /**
     * Change the selectedEntity to be the specified alertStatus is
     */
    const setSelectedAlertStatus = useCallback((alertStatusId: number) => {
        if (robot) {
            setSelectedAlertStatusId(alertStatusId);
            const options: RCAlertPreviewCompOptions = {
                location : SlideOutPanelLocation.RIGHT,
                compType : RCSlideOutPanelCompType.ALERT_PREVIEW,
                compProps: {
                    data: {
                        projectId: props.projectId,
                        alertId  : alertStatusId,
                        homeId   : robot?.homeId,
                        robotId  : robot.id,
                    },
                    context: {
                        userId: props.userId,
                        homeId: props.homeId,
                    },
                },
            };
            uiDispatch(showSlideOutPanel(options));
        }
    }, [robot, props.projectId, props.userId, props.homeId, uiDispatch]);



    /**
     * Localize the Robot Health data before passing it to the AlertStatusesTableComp for display
     */
    const localizeRobotHealthData = useMemo((): readonly RCAlertStatusesTableDataItem[] | undefined => {
        const alertStatuses: readonly AlertStatus[] | undefined = robot?.status?.alertStatuses;

        if (alertStatuses) {
            return alertStatuses.map((alertStatus: AlertStatus): RCAlertStatusesTableDataItem => {
                return {
                    actualValue : alertStatus,
                    displayValue: {
                        monitorId  : alertStatus.alertConditionId.toString(),
                        monitor    : `${alertStatus.alertConditionId.toString()} - ${alertStatus.name}`,
                        name       : alertStatus.name,
                        description: alertStatus.description,
                        severity   : alertStatus.severity
                            ? capitalizeAll(trans(RCUtils.getMonitorSeverityName(alertStatus.severity)))
                            : capitalizeFirst(trans('label.none')),
                        condition: RCUtils.getMonitorConditionAsString(robotCatalog, alertStatus.createdCondition, props.robotClass, trans, true),
                        createdAt: getDateTimeAndElapsedTime(alertStatus.createdAt, trans, false),
                    },
                    selected: selectedAlertStatusId === alertStatus.id,
                };
            });
        }

        return undefined;
    }, [trans, robotCatalog, robot, props.robotClass, selectedAlertStatusId]);



    // Create the table settings for the AlertsTable
    const alertsTableSettings = useMemo((): RCAlertStatusesTableProps['fieldsSettings'] => {
        return {
            fields: {
                monitorId: {
                    dataItemProp: 'monitorId',
                    label       : capitalizeAll(trans('label.monitor_id')),
                    sortable    : false,
                    hidden      : true,
                },
                monitor: {
                    dataItemProp: 'monitor',
                    label       : capitalizeAll(trans('label.triggered_monitor')),
                    sortable    : false,
                    hidden      : true,
                },
                name: {
                    dataItemProp: 'name',
                    label       : capitalizeAll(trans('label.alert')),
                    sortable    : false,
                },
                description: {
                    dataItemProp: 'description',
                    label       : capitalizeAll(trans('label.description')),
                    sortable    : false,
                    hidden      : true,
                },
                severity: {
                    dataItemProp: 'severity',
                    label       : capitalizeAll(trans('label.severity')),
                    sortable    : false,
                },
                condition: {
                    dataItemProp: 'conditions',
                    label       : capitalizeAll(trans('label.reason')),
                    sortable    : false,
                    hidden      : true,
                },
                createdAt: {
                    dataItemProp: 'createdAt',
                    label       : capitalizeAll(trans('label.since')),
                },
            },
        };
    }, [trans]);



    const isFetchingData = !robot;


    return (
        <>
            {/** Show RobotStatus and Alerts */}
            {project && robot && robotHealthCondition !== undefined && (
                <RCAlertStatusesTable
                    className={clsx(
                        props.className, robot.status && robot.status.alertStatuses.length > 0 && classes.alertsStatusPanel,
                    )}
                    robotName={props.title || capitalizeFirst(trans('label.robot_name_display', {
                        robot_name: robot.name || robot.id,
                    }))}
                    robotAggregatedHealthCondition={robotHealthCondition}
                    robotAggregatedHealthConditionDisplay={
                        capitalizeAll(trans(getRobotHealthConditionName(robotHealthCondition)))
                    }
                    listData={localizeRobotHealthData}
                    fieldsSettings={alertsTableSettings}
                    labels={{
                        healthConditionTitle: capitalizeAll(trans('label.robot_health_condition')),
                    }}
                    noDataMessageComp={(
                        <GenericMessageBox
                            title={trans('generic.no_data.title',
                                { term: capitalizeAll(trans('label.alert', { count: 2 })) })}
                        />
                    )}
                    emptyDataMessageComp={(
                        <GenericMessageBox
                            title={trans('generic.no_data.title', {
                                term: capitalizeAll(trans('label.alert', { count: 2 })),
                            })}
                        />
                    )}
                    onListItemSelected={(alert: AlertStatus) => {
                        setSelectedAlertStatus(alert.id);
                    }}
                />
            )}

            {isFetchingData && (
                <CircularProgress />
            )}
        </>
    );
};
